import * as React from 'react';
import apiService from '../../services/api';
import { DocumentExporter } from '../../word/utils/DocumentExporter';
import { DocumentReader } from '../../word/api/DocumentReader';
import { requestCache } from '../../services/RequestCache';
import './MasterCrossChecksInitiation.css';
import './ProjectSetup.css';

/** Log to console with timestamp (e.g. [5:23:47 PM] [LOG] message) for debugging initiation flow */
function logInitiation(message: string, data?: Record<string, unknown>): void {
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: '2-digit', second: '2-digit' });
  const payload = data ? ` ${JSON.stringify(data)}` : '';
  console.log(`[${time}] [LOG] [MasterCrossChecksInitiation] ${message}${payload}`);
}

export type MasterCrossChecksWorkflowVariant = 'master_cross_checks' | 'construction_loan';

interface MasterCrossChecksInitiationProps {
  projectId: string;
  onComplete: (subProjectId: string) => void;
  onCancel: () => void;
  /** Construction Loan Agreement matches web dashboard upload + track-changes follow-up; MCC skips TC gate. */
  workflowVariant?: MasterCrossChecksWorkflowVariant;
  /**
   * Called when the background auto-start cross-check fails with a validation error (HTTP 400).
   * Fires asynchronously after the user has already been navigated to the results view, so the
   * parent should navigate the user back and display the error message.
   */
  onError?: (message: string) => void;
}

type Phase =
  | 'name_input'
  | 'creating'
  | 'draft_question'
  | 'related_project'
  | 'identify_sides'
  | 'side_assignments'
  | 'start_cross_check';

interface SiblingSubProject {
  id: string;
  name: string;
}

export const MasterCrossChecksInitiation: React.FC<MasterCrossChecksInitiationProps> = ({
  projectId,
  onComplete,
  onCancel,
  workflowVariant = 'master_cross_checks',
  onError,
}) => {
  const [phase, setPhase] = React.useState<Phase>('name_input');
  const [subProjectName, setSubProjectName] = React.useState('');
  const [suggestedName, setSuggestedName] = React.useState('');
  const [createdSubProjectId, setCreatedSubProjectId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [progressMessage, setProgressMessage] = React.useState('');

  // Draft question / related project state
  const [siblingSubProjects, setSiblingSubProjects] = React.useState<SiblingSubProject[]>([]);
  const [selectedParentId, setSelectedParentId] = React.useState<string | null>(null);
  const [isLoadingSiblings, setIsLoadingSiblings] = React.useState(false);
  const [isLinking, setIsLinking] = React.useState(false);

  // Identify sides state
  const [uniqueAuthors, setUniqueAuthors] = React.useState<string[]>([]);
  const [sideAssignments, setSideAssignments] = React.useState<Record<string, 'our_side' | 'their_side'>>({});
  const [_isIdentifying, setIsIdentifying] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Start cross-check state
  const [isStartingCrossCheck, setIsStartingCrossCheck] = React.useState(false);

  const [uploadedClientFileName, setUploadedClientFileName] = React.useState<string | null>(null);
  const isConstructionLoanFlow = workflowVariant === 'construction_loan';

  React.useEffect(() => {
    const loadFilename = async () => {
      try {
        const filename = await DocumentExporter.getDocumentNameWithoutExtension();
        if (filename && filename !== 'document') {
          setSuggestedName(filename);
          setSubProjectName(filename);
        } else {
          setSuggestedName('New Sub-Project');
          setSubProjectName('New Sub-Project');
        }
      } catch {
        setSuggestedName('New Sub-Project');
        setSubProjectName('New Sub-Project');
      }
    };
    loadFilename();
  }, []);

  const handleCreate = async () => {
    if (!subProjectName.trim()) {
      setError('Please enter a sub-project name');
      return;
    }

    setPhase('creating');
    setError(null);

    try {
      // Step 1: Create sub-project
      setProgressMessage('Creating sub-project...');
      const createResponse = await apiService.createSubProject(projectId, subProjectName.trim());
      if (!createResponse.success || !createResponse.data) {
        throw new Error('Failed to create sub-project');
      }
      const newSubProjectId = createResponse.data.id;
      setCreatedSubProjectId(newSubProjectId);
      logInitiation('Sub-project created', { subProjectId: newSubProjectId, name: subProjectName.trim() });

      // Step 2: Copy master documents from parent project
      setProgressMessage('Copying master documents...');
      try {
        await apiService.copyMasterDocumentsToSubProject(newSubProjectId);
        logInitiation('Copy master documents completed');
      } catch (copyErr: any) {
        logInitiation('Copy master documents warning (continuing)', { message: String(copyErr) });
      }

      // Step 3: Upload current Word document as Client Requests
      setProgressMessage('Uploading document as Client Requests...');
      try {
        const documentFilename = await DocumentExporter.getDocumentName();
        const documentFile = await DocumentExporter.getDocumentAsFile(documentFilename);
        const uploadRes = await apiService.uploadDocument(
          projectId,
          newSubProjectId,
          documentFile,
          'Client Requests'
        );
        if (!uploadRes.success || !uploadRes.data) {
          throw new Error(uploadRes.message || 'Upload failed');
        }
        setUploadedClientFileName(documentFile.name || documentFilename || null);

        if (isConstructionLoanFlow) {
          setProgressMessage('Checking document for track changes...');
          const storagePath = (uploadRes.data as { storage_path?: string }).storage_path;
          const fileId = (uploadRes.data as { id?: string }).id;
          let hasTrackChanges = false;
          if (storagePath) {
            try {
              const tcRes = await apiService.analyzeTrackChanges(storagePath, projectId, fileId);
              const n = tcRes?.data?.paragraphs_with_track_changes ?? 0;
              hasTrackChanges = typeof n === 'number' ? n > 0 : false;
            } catch {
              // non-blocking: if analysis fails, still allow flow (dashboard treats similarly)
            }
          }
          if (!hasTrackChanges) {
            throw new Error(
              'This file does not contain track changes. Construction Loan Agreement requires client request files with track changes.'
            );
          }
        }
      } catch (uploadErr: any) {
        const uploadErrorMsg = uploadErr.response?.data?.detail || uploadErr.message || '';
        if (!uploadErrorMsg.includes('already exists') && !uploadErrorMsg.includes('duplicate')) {
          throw new Error(
            uploadErr.message?.includes('track changes')
              ? uploadErr.message
              : `Failed to upload document: ${uploadErrorMsg}`
          );
        }
        logInitiation('Upload document (file already exists, continuing)');
      }
      logInitiation('Client Requests document uploaded');

      // Step 3b (CL only): If no Sales Agreement PDF exists on the project, upload the
      // clean loan agreement text from the live Word document (reject-all OOXML semantics).
      if (isConstructionLoanFlow) {
        setProgressMessage('Sending loan agreement context...');
        try {
          const projectDetail = await apiService.getProjectDetail(projectId);
          const files: Array<{ file_type: string; status: string }> = projectDetail?.data?.files ?? [];
          const hasSalesAgreement = files.some(
            (f) => f.file_type === 'Sales Agreement' && f.status === 'Ready'
          );
          if (!hasSalesAgreement) {
            const { fullText, paragraphs } = await DocumentReader.getFullDocumentContextOriginal();
            if (fullText.trim()) {
              await apiService.saveLoanAgreementContext(projectId, fullText, paragraphs);
              logInitiation('Loan agreement inline context saved', { paragraphCount: paragraphs.length });
            } else {
              logInitiation('Loan agreement inline context is empty – skipping');
            }
          } else {
            logInitiation('Sales Agreement PDF exists – skipping inline context');
          }
        } catch (loanErr: any) {
          logInitiation('Warning: loan agreement context save failed (continuing)', { error: String(loanErr) });
        }
      }

      // Invalidate sub-projects cache so the list refreshes
      requestCache.invalidate(`sub-projects-${projectId}`);

      // Step 4: Show draft question
      setPhase('draft_question');
    } catch (err: any) {
      logInitiation('Error during sub-project creation', { status: err.response?.status, detail: err.response?.data?.detail || err.message });
      setError(err.response?.data?.detail || err.message || 'Failed to create sub-project');
      setPhase('name_input');
    }
  };

  const handleDraftYes = async () => {
    setIsLoadingSiblings(true);
    setPhase('related_project');
    setSelectedParentId(null);

    try {
      const response = await apiService.getProjectDetail(projectId);
      if (response.success && response.data?.sub_projects) {
        const siblings = response.data.sub_projects
          .filter((sp: any) => sp.id !== createdSubProjectId && !sp.sub_project_metadata?.parent)
          .map((sp: any) => ({ id: sp.id, name: sp.name }));
        setSiblingSubProjects(siblings);
      }
    } catch (err) {
      console.error('Error fetching sibling sub-projects:', err);
      setError('Failed to load existing client projects');
    } finally {
      setIsLoadingSiblings(false);
    }
  };

  const handleDraftNo = async () => {
    await proceedToIdentifySides();
  };

  const handleRelatedProjectContinue = async (parentId: string | null) => {
    if (!createdSubProjectId) return;

    if (parentId) {
      setIsLinking(true);
      try {
        await apiService.linkToParent(createdSubProjectId, parentId);
      } catch (err: any) {
        console.error('Error linking to parent:', err);
        setError(err.response?.data?.detail || 'Failed to link draft to parent');
        setIsLinking(false);
        return;
      }
      setIsLinking(false);
    }

    await proceedToIdentifySides();
  };

  const proceedToIdentifySides = async () => {
    if (!createdSubProjectId) return;

    setPhase('identify_sides');
    setIsIdentifying(true);
    setError(null);

    try {
      const authors = await apiService.identifySides(createdSubProjectId);
      logInitiation('Identify sides completed', { authorCount: authors.length });
      if (authors.length > 0) {
        setUniqueAuthors(authors);
        setPhase('side_assignments');
      } else {
        // No authors found - go to Start Cross-Check step (auto-start will run as Module A)
        logInitiation('No authors from identify-sides, showing Start Cross-Check step');
        setPhase('start_cross_check');
      }
    } catch (err: any) {
      console.error('Error identifying sides:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to identify authors';
      setError(errorMessage);
      // Still allow user to proceed even on error
      setPhase('side_assignments');
    } finally {
      setIsIdentifying(false);
    }
  };

  const handleSaveSideAssignments = async () => {
    if (!createdSubProjectId) return;

    setIsSaving(true);
    setError(null);
    logInitiation('Saving side assignments', { subProjectId: createdSubProjectId, authorCount: Object.keys(sideAssignments).length });

    try {
      await apiService.saveSideAssignments(createdSubProjectId, sideAssignments);
      logInitiation('Side assignments saved successfully (backend sets module_type=module-b)');
      setPhase('start_cross_check');
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      logInitiation('Error saving side assignments', { status: err.response?.status, detail });
      setError(detail || 'Failed to save side assignments');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartCrossCheck = async () => {
    if (!createdSubProjectId) return;

    setIsStartingCrossCheck(true);
    setError(null);
    logInitiation('Start Cross-Check clicked', { subProjectId: createdSubProjectId });

    // Transition to the project view immediately — CrossCheckResultsView handles all
    // live progress via its own polling loop. The HTTP call runs in the background.
    const subProjectId = createdSubProjectId;
    onComplete(subProjectId);

    // Extract TC paragraphs and fire auto-start as a background task (no await).
    (async () => {
      let preExtractedPayload: Parameters<typeof apiService.autoStartCrossCheck>[1] | undefined;

      if (isConstructionLoanFlow) {
        try {
          logInitiation('Extracting track-change paragraphs from live document via Office.js');
          const extracted = await DocumentReader.extractTrackChangedParagraphs();
          logInitiation('Office.js extraction complete', {
            totalParagraphs: extracted.allParagraphTexts.length,
            tcParagraphs: extracted.paragraphsWithTrackChanges.length,
          });
          if (extracted.paragraphsWithTrackChanges.length > 0) {
            preExtractedPayload = {
              pre_extracted_paragraphs: extracted.paragraphsWithTrackChanges.map((p) => ({
                paragraph_index: p.paragraphIndex,
                paragraph_xml: p.paragraphXml,
                original_text: p.originalText,
                edited_text: p.editedText,
                participants: p.participants,
              })),
              all_paragraph_texts: extracted.allParagraphTexts,
            };
          } else {
            logInitiation('No TC paragraphs via Office.js – falling back to file-based identification');
          }
        } catch (extractErr) {
          logInitiation('Office.js extraction failed – falling back to file-based identification', { error: String(extractErr) });
        }
      }

      try {
        await apiService.autoStartCrossCheck(subProjectId, preExtractedPayload);
        logInitiation('Auto-start cross-check call returned');
      } catch (err: any) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message;
        logInitiation('Auto-start cross-check call failed (user already in results view)', { status, error: detail });
        if (onError) {
          const msg =
            typeof detail === 'string' && detail.trim()
              ? detail
              : 'Cross-check workflow failed. Check backend logs or retry after the engine finishes starting.';
          onError(msg);
        }
      }
    })();
  };

  const handleLater = () => {
    if (createdSubProjectId) {
      onComplete(createdSubProjectId);
    }
  };

  const allAssigned = uniqueAuthors.length > 0 && uniqueAuthors.every((a) => sideAssignments[a]);

  // Phase: Name Input
  if (phase === 'name_input') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Create New Client Project</h3>
          <p className="mcc-initiation-description">
            Create a client project for this Word document
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="create-subproject-form">
          <div className="form-group">
            <label htmlFor="mcc-subproject-name">Project Name</label>
            <input
              id="mcc-subproject-name"
              type="text"
              dir="auto"
              value={subProjectName}
              onChange={(e) => setSubProjectName(e.target.value)}
              placeholder={suggestedName || 'Enter project name'}
              className="subproject-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && subProjectName.trim()) {
                  handleCreate();
                }
              }}
            />
          </div>
          <div className="mcc-initiation-buttons">
            <button onClick={onCancel} className="button-secondary">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="button-primary"
              disabled={!subProjectName.trim()}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Phase: Creating (loading)
  if (phase === 'creating') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Setting Up Project</h3>
        </div>
        <div className="project-setup-loading">
          <div className="spinner"></div>
          <span>{progressMessage}</span>
        </div>
      </div>
    );
  }

  // Phase: Draft Question
  if (phase === 'draft_question') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Track Changes Detected</h3>
          {isConstructionLoanFlow && uploadedClientFileName ? (
            <p className="mcc-initiation-description">
              The file &quot;{uploadedClientFileName}&quot; contains track changes.
            </p>
          ) : null}
          <p className="mcc-initiation-description">Is this the next draft iteration?</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="mcc-initiation-buttons">
          <button onClick={handleDraftNo} className="button-secondary">
            {isConstructionLoanFlow ? 'Totally New' : 'No'}
          </button>
          <button onClick={handleDraftYes} className="button-primary">
            Yes
          </button>
        </div>
      </div>
    );
  }

  // Phase: Related Project Selection
  if (phase === 'related_project') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Related Client Project</h3>
          <p className="mcc-initiation-description">
            What is the related existing client project of this draft?
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoadingSiblings ? (
          <div className="project-setup-loading">
            <div className="spinner"></div>
            <span>Loading client projects...</span>
          </div>
        ) : (
          <>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <select
                value={selectedParentId || ''}
                onChange={(e) => setSelectedParentId(e.target.value || null)}
                className="subproject-name-input"
                style={{ width: '100%' }}
              >
                <option value="">-- Select a client project --</option>
                {siblingSubProjects.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mcc-initiation-buttons">
              <button
                onClick={() => handleRelatedProjectContinue(null)}
                className="button-secondary"
                disabled={isLinking}
              >
                Totally New
              </button>
              <button
                onClick={() => handleRelatedProjectContinue(selectedParentId)}
                className="button-primary"
                disabled={isLinking || !selectedParentId}
              >
                {isLinking ? 'Linking...' : 'Continue'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Phase: Identifying Sides (loading)
  if (phase === 'identify_sides') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Extracting Authors</h3>
        </div>
        <div className="project-setup-loading">
          <div className="spinner"></div>
          <span>Extracting authors from track changes...</span>
        </div>
      </div>
    );
  }

  // Phase: Side Assignments
  if (phase === 'side_assignments') {
    return (
      <div className="mcc-initiation">
        <div className="mcc-initiation-header">
          <h3>Identify Sides</h3>
          <p className="mcc-initiation-description">
            {uniqueAuthors.length > 0
              ? `Found ${uniqueAuthors.length} unique author${uniqueAuthors.length !== 1 ? 's' : ''}. Assign each to a side.`
              : 'No authors found in track changes.'}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {uniqueAuthors.length > 0 ? (
          <>
            <div className="assignments-list">
              {uniqueAuthors.map((author) => (
                <div key={author} className="assignment-item">
                  <span className="author-name">{author}</span>
                  <div className="assignment-buttons">
                    <button
                      onClick={() =>
                        setSideAssignments((prev) => ({ ...prev, [author]: 'our_side' }))
                      }
                      className={`assignment-button ${sideAssignments[author] === 'our_side' ? 'active our-side' : ''}`}
                    >
                      Our Side
                    </button>
                    <button
                      onClick={() =>
                        setSideAssignments((prev) => ({ ...prev, [author]: 'their_side' }))
                      }
                      className={`assignment-button ${sideAssignments[author] === 'their_side' ? 'active their-side' : ''}`}
                    >
                      Their Side
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mcc-initiation-buttons">
              <button
                onClick={handleSaveSideAssignments}
                disabled={!allAssigned || isSaving}
                className="button-primary save-assignments-button"
              >
                {isSaving
                  ? 'Saving...'
                  : allAssigned
                    ? 'Save & Continue'
                    : `Assign all (${uniqueAuthors.filter((a) => sideAssignments[a]).length}/${uniqueAuthors.length})`}
              </button>
            </div>
          </>
        ) : (
          <div className="mcc-initiation-buttons">
            <button
              onClick={() => setPhase('start_cross_check')}
              className="button-primary"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    );
  }

  // Phase: Start Cross-Check (success + Start Cross-Check / Later)
  if (phase === 'start_cross_check') {
    const preStartSteps = [
      { key: 'identify_sides', label: 'Identify sides', status: 'complete' as const },
      { key: 'identify_requests', label: 'Identify client requests', status: 'pending' as const },
      { key: 'cross_check', label: 'Cross-check analysis', status: 'pending' as const },
    ];

    return (
      <div className="mcc-initiation">
        <div className="mcc-followup-steps">
          {preStartSteps.map((step) => (
            <div key={step.key} className={`mcc-followup-step mcc-followup-${step.status}`}>
              <span className="mcc-followup-arrow" aria-hidden>&#8250;</span>
              <span className="mcc-followup-icon" aria-hidden>
                {step.status === 'complete' ? '✓' : '○'}
              </span>
              <span className="mcc-followup-label">{step.label}</span>
            </div>
          ))}
        </div>

        <div className="mcc-success-block">
          <div className="mcc-success-icon" aria-hidden>
            <svg className="mcc-success-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mcc-success-title">File Uploaded Successfully!</h3>
          <p className="mcc-initiation-description">
            Click the button below to start the AI cross-check process and create smart cards for your client requests.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="mcc-initiation-buttons">
          <button
            onClick={handleLater}
            className="button-secondary"
            disabled={isStartingCrossCheck}
          >
            Later
          </button>
          <button
            onClick={handleStartCrossCheck}
            disabled={isStartingCrossCheck}
            className="mcc-button-start-cross-check"
          >
            {isStartingCrossCheck ? (
              'Starting...'
            ) : (
              <>
                <svg className="mcc-play-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Start Cross-Check
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return null;
};
